import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, Download, Pencil, Plus } from "lucide-react";
import ProductEditDrawer from "@/pages/farm/ProductEditDrawer";

function CsvUploadCard({ onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const downloadTemplate = async () => {
    setError("");
    try {
      const { data } = await api.get("/farm/products/csv-template");
      const blob = new Blob([data.body], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.filename || "products-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.response?.data?.detail || "Could not fetch template"); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/farm/products/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      await onUploaded?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="card-flat p-5" data-testid="csv-upload-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Bulk product upload</p>
          <h3 className="font-display text-2xl mt-1">Upload products from a CSV</h3>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1 max-w-xl">Required columns: <code className="font-mono text-xs">name, category, price, unit, stock</code>. Optional: <code className="font-mono text-xs">image_url, description</code>. Each row becomes a product scoped to your farm.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="btn-secondary text-sm" data-testid="csv-template-btn">
            <Download size={14}/> Download template
          </button>
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary text-sm" data-testid="csv-upload-btn">
            <Upload size={14}/> {busy ? "Uploading…" : "Upload CSV"}
          </button>
          <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={onPick} className="hidden" data-testid="csv-file-input"/>
        </div>
      </div>
      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800 mt-4" data-testid="csv-error">{error}</div>}
      {result && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 p-3 rounded text-sm text-emerald-800" data-testid="csv-result">
          {result.summary}
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-xs list-disc pl-5" data-testid="csv-errors">
              {result.errors.slice(0, 10).map((e, i) => <li key={i}>Line {e.line}: {e.reason}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function InventoryTab({ canEdit }) {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({});
  const [editProduct, setEditProduct] = useState(null);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const load = () => api.get("/farm/inventory").then(r => setInv(r.data)).catch(e => setError(e.response?.data?.detail || "Load error"));
  useEffect(() => { load(); }, []);

  const saveDrop = async (slug) => {
    const val = parseInt(edits[`drop:${slug}`], 10); if (isNaN(val)) return;
    setBusy(true); setError("");
    try { await api.post(`/farm/inventory/drops/${slug}`, { total_inventory: val }); await load(); setEdits(e => { const n = {...e}; delete n[`drop:${slug}`]; return n; }); }
    catch (e) { setError(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!inv) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="inv-loading">Loading inventory…</div>;
  return (
    <div className="space-y-8" data-testid="farm-inventory-tab">
      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="inv-error">{error}</div>}
      {canEdit && <CsvUploadCard onUploaded={load}/>}
      <div>
        <h3 className="font-display text-2xl mb-3">Drops <span className="text-sm text-[var(--ff-text-muted)] font-sans">({inv.drops.length})</span></h3>
        {inv.drops.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] card-flat p-6">No drops yet — ask the platform team to seed one for your farm.</p>}
        <div className="space-y-3">
          {inv.drops.map(d => (
            <div key={d.slug} className="card-flat p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`drop-${d.slug}`}>
              <div className="flex-1">
                <p className="font-display text-lg">{d.name}</p>
                <p className="text-xs text-[var(--ff-text-muted)] mt-1">Drops {d.drops_at?.slice(0,10)} · ${d.price}</p>
                <p className="text-xs mt-2">Sold {d.sold||0} · Reserved {d.reserved||0} · Total {d.total_inventory}</p>
              </div>
              {canEdit && (
                <div className="flex gap-2 items-center">
                  <input type="number" min="0" defaultValue={d.total_inventory}
                    onChange={e => setEdits(p => ({...p, [`drop:${d.slug}`]: e.target.value}))}
                    className="input-base w-28" data-testid={`drop-inv-input-${d.slug}`}/>
                  <button disabled={busy} onClick={() => saveDrop(d.slug)} className="btn-primary text-sm" data-testid={`drop-save-${d.slug}`}>Save</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-2xl">Products <span className="text-sm text-[var(--ff-text-muted)] font-sans">({inv.products.length})</span></h3>
          {canEdit && (
            <button onClick={() => setCreatingProduct(true)} className="btn-primary text-sm inline-flex items-center gap-1" data-testid="product-new-btn">
              <Plus size={14}/> New product
            </button>
          )}
        </div>
        <div className="card-flat divide-y">
          {inv.products.map(p => (
            <div key={p.id} className={`p-4 flex items-center gap-4 ${p.active === false ? "opacity-60" : ""}`} data-testid={`product-${p.id}`}>
              <img src={p.image_url} className="w-12 h-12 rounded object-cover bg-stone-100" alt={p.name} onError={e => { e.currentTarget.style.visibility = "hidden"; }}/>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm flex items-center gap-2">
                  {p.name}
                  {p.active === false && <span className="text-[10px] uppercase tracking-wider bg-stone-200 text-stone-700 px-1.5 py-0.5 rounded">Hidden</span>}
                </p>
                <p className="text-xs text-[var(--ff-text-muted)]">${p.price?.toFixed(2)} / {p.unit} · {p.category} · stock {p.stock}</p>
              </div>
              {canEdit && (
                <button onClick={() => setEditProduct(p)} className="btn-secondary text-sm" data-testid={`product-edit-${p.id}`}>
                  <Pencil size={14}/> Edit
                </button>
              )}
            </div>
          ))}
          {inv.products.length === 0 && <p className="p-6 text-sm text-[var(--ff-text-muted)]">No products yet — use the bulk upload above or click Edit on a sample row to get started.</p>}
        </div>
      </div>

      {editProduct && (
        <ProductEditDrawer
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSaved={() => { setEditProduct(null); load(); }}
          onDeleted={() => { setEditProduct(null); load(); }}
        />
      )}
      {creatingProduct && (
        <ProductEditDrawer
          product={null}
          onClose={() => setCreatingProduct(false)}
          onSaved={() => { setCreatingProduct(false); load(); }}
        />
      )}
    </div>
  );
}
